import { EnvironmentOutlined } from '@ant-design/icons';
import { type Comune } from '@framework/api-client/api/inquiries/schemas/comune.ts';
import { type Provincia } from '@framework/api-client/api/inquiries/schemas/provincia.ts';
import { RequiredRule } from '@framework/engine/schema/rules/repository/required/Required.rule.ts';
import { tran } from '@framework/engine/schema/translations/schema/TranslationSchema.ts';
import { type IWidgetType } from '@framework/engine/schema/widgets/IWidgetType.ts';
import {
  WidgetCategory,
  WidgetMainType,
} from '@framework/engine/schema/widgets/Widget.ts';
import { get } from 'lodash';
import { ProvinceWidgetSelectComponent } from './components/ProvinceWidgetSelectComponent.tsx';
import {
  createProvinceWidgetSchema,
  getProvinceDataFieldName,
  getTownDataFieldName,
} from './createProvinceWidgetSchema.ts';
import { getProvinceWidgetReference } from './getProvinceWidgetReference.ts';
import { getProvinceWidgetSiblingReferences } from './getProvinceWidgetSiblingReferences.ts';
import { type IProvinceWidget, WidgetProvinceType } from './IProvinceWidget.ts';
import { ProvinceWidgetComponent } from './ProvinceWidgetComponent.tsx';
import { ProvinceWidgetConfigComponent } from './ProvinceWidgetConfigComponent.tsx';
import { ProvinceWidgetConfigSchema } from './ProvinceWidgetConfigSchema.ts';

declare module '@framework/engine/schema/widgets/Widget.ts' {
  export enum WidgetType {
    Province = 'province',
  }
}

/**
 * Struttura del widget di download
 */
export const ProvinceWidgetType: IWidgetType<IProvinceWidget> = {
  type: WidgetProvinceType,
  mainType: WidgetMainType.Input,
  category: WidgetCategory.Input,
  name: 'Città e Provincia',
  icon: <EnvironmentOutlined />,
  hasValue: true,
  getReference: getProvinceWidgetReference,
  getSiblingReferences: getProvinceWidgetSiblingReferences,
  formComponent: ProvinceWidgetComponent,
  previewComponent: ProvinceWidgetSelectComponent,
  configValidator: ProvinceWidgetConfigSchema,
  configComponent: ProvinceWidgetConfigComponent,
  createFormSchema: createProvinceWidgetSchema,
  // Traduzioni
  translationSchema: tran.widget({
    label: tran.rte(),
    provinceLabel: tran.rte(),
    townLabel: tran.rte(),
    provincePlaceholder: tran.string(),
    townPlaceholder: tran.string(),
    provinceHelpText: tran.rte(),
    townHelpText: tran.rte(),
  }),
  rules: [RequiredRule],
  hydrate: ({ context, path, widget }) => {
    try {
      // Recupero i dati della provincia
      const provinceDataFieldName = getProvinceDataFieldName(path);
      const provinceData = get(context.values, provinceDataFieldName) as
        | Provincia
        | undefined;
      const provinceLabelValue = provinceData?.nome;

      // Recupero i dati del comune
      const townDataFieldName = getTownDataFieldName(path);
      const townData = get(context.values, townDataFieldName) as
        | Comune
        | undefined;
      const townLabelValue = townData?.nome;

      // Li ritorno, il campo andrà a sovrascrivere il valore ritornato.
      return {
        value: {
          province: provinceLabelValue,
          town: townLabelValue,
        },
      };
    } catch {
      // biome-ignore format: <tbd>
      console.error(`[ProvinceWidget] Errore durante l'hydrate del widget ${path}`, { context, path, widget });
      return {
        value: {
          province: 'N/A',
          town: 'N/A',
        },
      };
    }
  },
};
